import type { CreateCampaignInput, CreateVariantPriceInput } from '~/types/graphql';
import type { MessageToast } from '~/modules/campaign/types';

import {
  CAMPAIGN_PRIORITY,
  CAMPAIGN_STATUS,
  CAMPAIGN_TYPE,
  DEFAULT_SETTING_TARGET,
} from '~/constants/campaign';
import { IVariantType } from '~/modules/campaign/types';

export const PRODUCT_PRICE_EXPERIMENT_NAME = 'Product price experiment';

export const CAMPAIGN_PRICE_SAVE_BAR_ID = 'campaign-price-update';

/** Saving a live test restarts it, so a blank Variant price would go live the same way Start forbids. */
export const PRODUCT_PRICE_LIVE_SAVE_BLOCKED_MSG = 'Set up Variant price for every variant to save';

/** The test duplicates the product at the Variant's prices, so the server refuses one sold for free. */
export const PRODUCT_PRICE_ZERO_SAVE_BLOCKED_MSG = 'Variant price must be greater than 0';

export const PLATFORM_PRODUCT_DETAILS_QUERY_KEY = 'PlatformProductDetails';

export const VARIANT_PRICE_SKELETON_WIDTH = '72px';

/** Polaris' own Card default, spelled out so a card can drop to `0` and back without guessing. */
export const CARD_PADDING = '400';

/** The two amounts a Shopify product variant carries, as `PlatformProductVariantSelect` spells them. */
export const PLATFORM_PRODUCT_PRICE_FIELD = {
  PRICE: 'price',
  COMPARE_PRICE: 'compareAtPrice',
} as const;

/**
 * Off until a price test can run more than one product end to end. It gates both halves of the UI
 * that would offer a second one: the picker's multi-select and the table's Add product footer.
 */
export const IS_PRICE_MULTI_PRODUCT_ENABLED = false;

export const PRICE_SETUP_GUIDE_SEEN_ACTION = 'STATUS';

export const DEFAULT_TRAFFIC_RATE = '0.5';

export const PRODUCT_PRICE_VARIANT_ACTION = {
  PICK: 'PICK',
  DUPLICATE: 'DUPLICATE',
  REMOVE: 'REMOVE',
  SYNC: 'SYNC',
} as const;

export type IProductPriceVariantAction =
  (typeof PRODUCT_PRICE_VARIANT_ACTION)[keyof typeof PRODUCT_PRICE_VARIANT_ACTION];

export const PRODUCT_PRICE_VARIANT_MSG: Record<IProductPriceVariantAction, MessageToast> = {
  SYNC: {
    SUCCESS: 'Product synced',
    FAILED: 'Failed to sync product',
  },
  PICK: {
    SUCCESS: 'Product selected',
    FAILED: 'Failed to select product',
  },
  DUPLICATE: {
    SUCCESS: 'Product duplicated',
    FAILED: 'Failed to duplicate control product',
  },
  REMOVE: {
    SUCCESS: 'Product removed',
    FAILED: 'Failed to remove product',
  },
};

export const PRODUCT_PRICE_CAMPAIGN_PRESET: CreateCampaignInput = {
  name: '',
  status: CAMPAIGN_STATUS.DRAFT,
  priority: CAMPAIGN_PRIORITY.REVENUE,
  type: CAMPAIGN_TYPE.PRICE,
  storeDomain: '',
  targetCountries: DEFAULT_SETTING_TARGET,
  targetDevices: DEFAULT_SETTING_TARGET,
  targetChannels: DEFAULT_SETTING_TARGET,
  targetVisitorTypes: DEFAULT_SETTING_TARGET,
};

export const EMPTY_VARIANT_BUTTON_LABEL: Record<IVariantType, string> = {
  [IVariantType.VARIANT_A]: 'Choose product',
  [IVariantType.VARIANT_B]: 'Set price',
};

// Spread straight into `variantPriceCreate`'s input when a new Price experiment is scaffolded, so this
// has to stay shaped like `CreateVariantPriceInput` — never `VariantPrice` itself, which carries fields
// (like `sourceProductBaseID`) that input has no place for and would reject as unknown.
export const VARIANT_PRICE_PRESET: Omit<CreateVariantPriceInput, 'variantID'> = {
  baseID: '',
  handle: '',
  status: 'UNLISTED',
};
